import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { X, InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { ProductSize } from "@/types/product";
import { SingleImageUpload } from "@/components/ui/single-image-upload";
import { FormMessage } from "@/components/ui/form";
import { type ProductFormData } from "@/types/product";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ProductSizesProps {
  sizes: ProductFormData['sizes'];
  onChange: (sizes: ProductFormData['sizes']) => void;
  error?: string;
}

export function ProductSizes({ sizes = [], onChange, error }: ProductSizesProps) {
  const [newSize, setNewSize] = useState<ProductFormData['sizes'][number]>({
    size_name: "",
    price: 0,
    image_url: null,
    use_product_image: true
  });

  const handleAddSize = () => {
    if (!newSize.size_name || newSize.price <= 0) {
      return;
    }
    onChange([...sizes, newSize]);
    setNewSize({
      size_name: "",
      price: 0,
      image_url: null,
      use_product_image: true
    });
  };

  const handleRemoveSize = (index: number) => {
    if (sizes.length === 1) {
      toast.error("Cannot remove the last size option");
      return;
    }
    onChange(sizes.filter((_, i) => i !== index));
  };

  const handleSizeChange = (index: number, field: keyof ProductFormData['sizes'][number], value: any) => {
    const newSizes = [...(sizes || [])];
    
    if (!newSizes[index]) {
      newSizes[index] = {
        size_name: "",
        price: 0,
        image_url: null,
        use_product_image: true
      };
    }
    
    newSizes[index] = {
      ...newSizes[index],
      [field]: value
    };
    
    if (index === 0 && field === 'image_url') {
      newSizes.forEach((size, i) => {
        if (i > 0 && size.use_product_image) {
          size.image_url = value;
        }
      });
    }
    
    onChange(newSizes);
  };

  // Check if main product image is missing
  const isMainImageMissing = sizes.length > 0 && !sizes[0]?.image_url;

  return (
    <div className="space-y-4">
      {/* Warning about image upload */}
      <Alert variant="warning" className="mb-4">
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>Important!</AlertTitle>
        <AlertDescription>
          <p>Please make sure to <strong>upload and save images</strong> for your product:</p>
          <ul className="list-disc pl-5 mt-2">
            <li>Adding a <strong>product image</strong> is recommended but not required</li>
            <li>Images are only saved when you click "Create Product" or "Update Product"</li>
            <li>If you leave this page without saving, your image uploads will be lost</li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {(sizes || []).map((size, index) => (
          <div
            key={index}
            className={cn(
              "flex flex-col gap-4 p-4 border rounded-lg relative",
              error && "border-destructive",
              index === 0 && isMainImageMissing && "border-orange-500 bg-orange-50"
            )}
          >
            {index === 0 && isMainImageMissing && (
              <div className="bg-orange-100 text-orange-800 p-2 rounded-md text-sm mb-2 font-medium">
                Please upload a main product image!
              </div>
            )}

            {(sizes || []).length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2"
                onClick={() => handleRemoveSize(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Size Name</Label>
                <Input
                  placeholder="e.g. Small, Medium, Large"
                  value={size?.size_name || ""}
                  onChange={(e) =>
                    handleSizeChange(index, 'size_name', e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Price</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={typeof size?.price === 'number' ? size.price : ''}
                  onChange={(e) =>
                    handleSizeChange(index, 'price', parseFloat(e.target.value) || 0)
                  }
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={cn(
                  index === 0 && "font-bold",
                  index === 0 && isMainImageMissing && "text-orange-700"
                )}>
                  {index === 0 ? 'Main Product Image' : 'Image'}
                  {index === 0 && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {index > 0 && (
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={!!size?.use_product_image}
                      onCheckedChange={(checked) => {
                        handleSizeChange(index, 'use_product_image', checked);
                        if (checked) {
                          handleSizeChange(index, 'image_url', sizes[0]?.image_url || null);
                        }
                      }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Use main product image
                    </span>
                  </div>
                )}
              </div>
              {(!size?.use_product_image || index === 0) && (
                <>
                  <SingleImageUpload
                    value={size?.image_url || null}
                    initialImageUrl={size?.image_url ?? undefined}
                    onChange={(url) => {
                      console.log(`Image URL updated for size ${index}:`, url);
                      handleSizeChange(index, 'image_url', url);
                    }}
                  />
                  {index === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      This main image will be displayed in search results and product listings.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {error && (
        <div className="text-sm font-medium text-destructive mt-2 p-2 border border-destructive rounded bg-destructive/10">
          {error}
        </div>
      )}

      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <Label>New Size Name</Label>
          <Input
            value={newSize.size_name}
            onChange={(e) => setNewSize({ ...newSize, size_name: e.target.value })}
            placeholder="e.g. Small, Medium, Large"
          />
        </div>
        <div className="w-32">
          <Label>Price</Label>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={newSize.price || ''}
            onChange={(e) => setNewSize({ ...newSize, price: parseFloat(e.target.value) || 0 })}
            placeholder="Price"
          />
        </div>
        <Button 
          type="button"
          onClick={handleAddSize}
        >
          Add Size
        </Button>
      </div>
    </div>
  );
}